
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import { createClient } from '@/utils/supabase/component';
import { validatePassword } from '@/utils/passwordPolicy';
import PasswordStrengthHints from '@/components/PasswordStrengthHints';
import Seo from '@/components/Seo';
export default function ResetPassword() {
    const supabase = createClient();
    const router = useRouter();
    const { t } = useLocale('login');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSessionReady, setIsSessionReady] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(true);
    const [sessionError, setSessionError] = useState<string | null>(null);
    const [message, setMessage] = useState<{
        type: 'error' | 'success';
        text: string;
    } | null>(null);
    useEffect(() => {
        const initSession = async () => {
            // Support query param code flow (Supabase may redirect with ?code=)
            try {
                const searchParams = new URLSearchParams(window.location.search);
                const queryError = searchParams.get('error');
                const queryErrorDescription = searchParams.get('error_description');
                const code = searchParams.get('code');
                if (queryError) {
                    setSessionError(queryErrorDescription || 'Authentication error occurred');
                    setSessionLoading(false);
                    return;
                }
                if (code) {
                    const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
                    if (exchangeError) {
                        setSessionError('Your reset link has expired or is invalid. Please request a new password reset.');
                        setSessionLoading(false);
                        return;
                    }
                    const { data: { session }, } = await supabase.auth.getSession();
                    if (session) {
                        setIsSessionReady(true);
                        // Clear query params from URL for security
                        window.history.replaceState(null, '', window.location.pathname);
                        setSessionLoading(false);
                        return;
                    }
                }
            }
            catch (e) {
                // ignore and continue to hash handling
            }
            // Check if we already have a valid session (from PKCE flow)
            const { data: { session }, } = await supabase.auth.getSession();
            if (session) {
                setIsSessionReady(true);
            }
            else {
                setSessionError('No valid reset token found. Please use the link from your email or request a new password reset.');
            }
            setSessionLoading(false);
        };
        initSession();
    }, [supabase]);
    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        // Check if session is ready
        if (!isSessionReady) {
            setMessage({
                type: 'error',
                text: 'Session is not ready. Please use the link from your email or request a new password reset.',
            });
            return;
        }
        if (password !== confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match' });
            return;
        }
        const policyResult = validatePassword(password);
        if (!policyResult.isValid) {
            setMessage({
                type: 'error',
                text: policyResult.firstError ??
                    'Password does not meet the security requirements.',
            });
            return;
        }
        setLoading(true);
        setMessage(null);
        try {
            // Double-check we have a session before updating
            const { data: { session }, } = await supabase.auth.getSession();
            if (!session) {
                setMessage({
                    type: 'error',
                    text: 'Auth session missing! Please request a new password reset link.',
                });
                setIsSessionReady(false);
                return;
            }
            const { error } = await supabase.auth.updateUser({
                password: password,
            });
            if (error) {
                // Check if the error is related to session
                if (error.message.includes('session') ||
                    error.message.includes('Auth')) {
                    setMessage({
                        type: 'error',
                        text: 'Your reset link has expired or is invalid. Please request a new password reset.',
                    });
                    setIsSessionReady(false);
                }
                else {
                    setMessage({ type: 'error', text: error.message });
                }
            }
            else {
                setMessage({
                    type: 'success',
                    text: 'Password updated successfully! Redirecting to login...',
                });
                // Sign out and redirect to login
                setTimeout(async () => {
                    await supabase.auth.signOut({ scope: 'global' });
                    router.push('/login');
                }, 2000);
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            setLoading(false);
        }
    };
    return (<div className='flex flex-col items-center justify-center min-h-screen py-12 dark:bg-slate-900'>
      <Seo templateTitle='Reset Password'/>
      <div className='w-full max-w-md'>
        <div>
          <Link href='/' className='' passHref legacyBehavior>
            <span className='flex justify-center text-2xl font-extrabold text-primary-700'>
              <Image src='/images/marky.png' width='250' height='36' alt='Easy-Peasy.ai logo'/>
            </span>
          </Link>
          <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-slate-200'>
            Reset your password
          </h2>
          <p className='mt-2 text-center text-sm text-gray-600 dark:text-slate-400'>
            Enter your new password below
          </p>
        </div>

        <div className='mt-8'>
          {/* Show loading state while checking session */}
          {sessionLoading && (<div className='mb-4 p-4 rounded-md bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'>
              <div className='flex items-center'>
                <svg className='animate-spin h-5 w-5 mr-3 text-blue-600' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                </svg>
                Verifying your reset link...
              </div>
            </div>)}

          {/* Show session error prominently if there's one */}
          {!sessionLoading && sessionError && (<div className='mb-4 p-4 rounded-md bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg className='h-5 w-5 text-red-400' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
                    <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z' clipRule='evenodd'/>
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3 className='text-sm font-medium text-red-800 dark:text-red-400'>
                    Authentication Error
                  </h3>
                  <div className='mt-2 text-sm text-red-700 dark:text-red-300'>
                    <p>{sessionError}</p>
                  </div>
                  <div className='mt-4'>
                    <Link href='auth/forgot-password' className='text-sm font-medium text-red-800 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 underline'>
                      Request a new password reset link →
                    </Link>
                  </div>
                </div>
              </div>
            </div>)}

          {message && (<div className={`mb-4 p-4 rounded-md ${message.type === 'error'
                ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                : 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400'}`}>
              {message.text}
            </div>)}

          <form onSubmit={handleResetPassword} className='space-y-6'>
            <div>
              <label htmlFor='password' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                New Password
              </label>
              <input id='password' name='password' type='password' autoComplete='new-password' required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} disabled={!isSessionReady || loading || sessionLoading} aria-describedby='reset-password-requirements' className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed' placeholder='Enter new password'/>
              <div id='reset-password-requirements'>
                <PasswordStrengthHints rules={validatePassword(password).rules}/>
              </div>
            </div>

            <div>
              <label htmlFor='confirmPassword' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                Confirm New Password
              </label>
              <input id='confirmPassword' name='confirmPassword' type='password' autoComplete='new-password' required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={!isSessionReady || loading || sessionLoading} className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed' placeholder='Confirm new password'/>
            </div>

            <div>
              <button type='submit' disabled={loading || !isSessionReady || sessionLoading} className='flex w-full justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'>
                {loading
            ? 'Updating password...'
            : sessionLoading
                ? 'Verifying session...'
                : !isSessionReady
                    ? 'Session not ready'
                    : 'Update password'}
              </button>
            </div>
          </form>

          <div className='mt-6 text-center'>
            <Link href='/login' className='text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400'>
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>);
}

    async function __Next_Translate__getStaticProps__1a13a29a0ad__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/auth/reset-password',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a13a29a0ad__ as getStaticProps }
  